import React, { useState, useRef, useEffect } from "react";
import { Search } from "lucide-react";

export default function SearchableSelectInput({
  label,
  name,
  value,
  onChange,
  options = [],
  className = "",
  labelClass = "",
  inputClass = "",
  required = false,
  error = "",
  disabled = false,
  placeholder = "Search...",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange({ target: { name, value: option.value } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && (
        <label htmlFor={name} className={`block text-sm font-medium mb-1 ${labelClass}`}>
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}

      <div className="relative">
        <div
          className={`w-full border rounded px-3 py-2 cursor-pointer flex items-center justify-between bg-white ${inputClass} ${
            error ? "border-red-500 ring-red-500" : "border-gray-300"
          } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
          onClick={handleInputClick}
        >
          {isOpen ? (
            <div className="flex items-center w-full">
              <Search className="w-4 h-4 text-gray-400 mr-2" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={placeholder}
                className="outline-none flex-1 bg-transparent"
                disabled={disabled}
              />
            </div>
          ) : (
            <>
              {selectedOption ? (
                <div className="flex items-center space-x-2">
                  {selectedOption.tagimage && (
                    <img src={selectedOption.tagimage} alt="" className="w-6 h-4" />
                  )}
                  <span className="text-gray-700">{selectedOption.name}</span>
                </div>
              ) : (
                <span className="text-gray-400">Select {label}</span>
              )}
            </>
          )}

          <svg
            className={`w-4 h-4 transform transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            } ${disabled ? "text-gray-400" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {isOpen && !disabled && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <li
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                  className="px-4 py-2 hover:bg-red-100 cursor-pointer flex items-center space-x-2"
                >
                  {opt.tagimage && (
                    <img src={opt.tagimage} alt="" className="w-6 h-4" />
                  )}
                  <span>{opt.name}</span>
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500 text-center">
                No options found for "{searchTerm}"
              </li>
            )}
          </ul>
        )}
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
